import { useT } from "../lang/LangContext";
import defjamIcon from "../images/defjam-icon.jpg";
import nuwaAppEn from "../images/nuwa-app-en.png";
import project8Icon from "../images/project8-icon.jpg";
import simpsonsIcon from "../images/simpsons-icon.jpg";
import switchHeroIcon from "../images/switch-hero-icon.jpg";
import { SectionHeading } from "./SectionHeading";

const TAGS: [string, string][] = [
  ["PHOTO LOGGING", "REGISTRO POR FOTO"],
  ["VOICE + FREE TEXT", "VOZ + TEXTO LIVRE"],
  ["MEAL PLANNING", "PLANEJAMENTO DE REFEIÇÕES"],
  ["HYDRATION", "HIDRATAÇÃO"],
];

type SwitchProject = {
  name: string;
  icon: string;
  kind: [string, string];
  description: [string, string];
  tags: [string, string][];
  repo: string;
  installer?: string;
};

const SWITCH_PROJECTS: SwitchProject[] = [
  {
    name: "Switch Hero",
    icon: switchHeroIcon,
    kind: ["ORIGINAL HOMEBREW", "HOMEBREW ORIGINAL"],
    description: [
      "A five-fret rhythm game written from scratch in C++17 and SDL2. It plays Clone Hero song folders, downloads charts in-game and seats up to four players in split screen on Joy-Cons, a Pro Controller or a Wii guitar.",
      "Um jogo de ritmo de cinco trastes escrito do zero em C++17 e SDL2. Toca pastas de músicas do Clone Hero, baixa charts dentro do jogo e comporta até quatro jogadores em tela dividida com Joy-Cons, Pro Controller ou uma guitarra de Wii.",
    ],
    tags: [
      ["C++17", "C++17"],
      ["SDL2", "SDL2"],
      ["4P SPLIT SCREEN", "TELA DIVIDIDA 4P"],
    ],
    repo: "https://github.com/R-drg/switch-hero",
  },
  {
    name: "Def Jam: Fight for NY",
    icon: defjamIcon,
    kind: ["XBOX PORT", "PORT DE XBOX"],
    description: [
      "The 2004 Xbox fighter, statically recompiled from the original executable. No emulator: the code is lifted to C and runs on a runtime that stands in for the Xbox kernel, its NV2A GPU, audio and controllers. Also builds for Linux.",
      "O jogo de luta de Xbox de 2004, recompilado estaticamente a partir do executável original. Sem emulador: o código é convertido para C e roda sobre um runtime que substitui o kernel do Xbox, a GPU NV2A, o áudio e os controles. Também compila para Linux.",
    ],
    tags: [
      ["C", "C"],
      ["STATIC RECOMP", "RECOMPILAÇÃO ESTÁTICA"],
      ["60 FPS", "60 FPS"],
    ],
    repo: "https://github.com/R-drg/defjam-sw",
    installer: "https://r-drg.github.io/defjam-sw-installer/",
  },
  {
    name: "The Simpsons Game",
    icon: simpsonsIcon,
    kind: ["XBOX 360 PORT", "PORT DE XBOX 360"],
    description: [
      "The 2007 Xbox 360 game, its PowerPC code recompiled to C++ with ReXGlue and drawn by a native Vulkan renderer on NVK (Mesa). It reuses the Switch layer built for Project 8 and holds a locked 30 FPS.",
      "O jogo de Xbox 360 de 2007, com o código PowerPC recompilado para C++ com ReXGlue e desenhado por um renderizador Vulkan nativo no NVK (Mesa). Reaproveita a camada de Switch feita para o Project 8 e mantém 30 FPS travados.",
    ],
    tags: [
      ["C++", "C++"],
      ["REXGLUE", "REXGLUE"],
      ["VULKAN", "VULKAN"],
    ],
    repo: "https://github.com/R-drg/the-simpsons-game-nx",
    installer: "https://r-drg.github.io/the-simpsons-game-nx-installer/",
  },
  {
    name: "Tony Hawk's Project 8",
    icon: project8Icon,
    kind: ["XBOX 360 PORT", "PORT DE XBOX 360"],
    description: [
      "The 2006 Xbox 360 skater, recompiled to C++ with ReXGlue and running on a native Vulkan renderer at a steady 30 FPS. The first of my 360 ports, and the base the Simpsons one is built on.",
      "O jogo de skate de Xbox 360 de 2006, recompilado para C++ com ReXGlue e rodando em um renderizador Vulkan nativo a 30 FPS estáveis. O primeiro dos meus ports de 360 e a base do port dos Simpsons.",
    ],
    tags: [
      ["C++", "C++"],
      ["REXGLUE", "REXGLUE"],
      ["VULKAN", "VULKAN"],
    ],
    repo: "https://github.com/R-drg/project8-sw",
    installer: "https://r-drg.github.io/project8-sw-installer/",
  },
];

export function Projects() {
  const t = useT();

  return (
    <section
      id="projects"
      style={{ maxWidth: 1180, margin: "0 auto", padding: "36px 20px 64px" }}
    >
      <SectionHeading index="02">{t("Projects", "Projetos")}</SectionHeading>
      <div className="projects-grid">
        <div
          className="project-feature"
          style={{
            minWidth: 0,
            border: "3px solid var(--fg)",
            background: "var(--card)",
            boxShadow: "10px 10px 0 var(--accent)",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              borderBottom: "3px solid var(--fg)",
              padding: "10px 16px",
              background: "var(--fg)",
              color: "var(--bg)",
            }}
          >
            <span
              style={{
                fontFamily: "'Archivo Black',sans-serif",
                fontSize: 13,
                letterSpacing: "0.1em",
              }}
            >
              01 · NÜWA / NUWAFIT.COM
            </span>
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.14em",
                background: "var(--bg)",
                color: "var(--fg)",
                padding: "4px 8px",
              }}
            >
              {t("SOLO FOUNDER · ENGINEER", "FUNDADOR SOLO · ENGENHEIRO")}
            </span>
          </div>
          <div className="project-feature__body">
            <div style={{ padding: 26, minWidth: 0 }}>
              <h3
                style={{
                  margin: "0 0 12px",
                  fontFamily: "'Archivo Black',sans-serif",
                  fontSize: "clamp(24px,3.2vw,38px)",
                  lineHeight: 1.02,
                  letterSpacing: "-0.03em",
                  textTransform: "uppercase",
                }}
              >
                {t(
                  "An AI companion for daily nutrition",
                  "Um companheiro de IA para a nutrição diária"
                )}
              </h3>
              <p style={{ margin: "0 0 16px", fontSize: 15, lineHeight: 1.65 }}>
                {t(
                  "Nüwa removes the friction of conventional calorie tracking. Log a meal by photo, free text, or voice instead of digging through a food database. Then plan the meals ahead against your own schedule and goals.",
                  "O Nüwa elimina o atrito do controle de calorias tradicional. Registre uma refeição por foto, texto livre ou voz em vez de garimpar um banco de alimentos. Depois planeje as próximas refeições de acordo com sua rotina e suas metas."
                )}
              </p>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: 15,
                  lineHeight: 1.65,
                  borderLeft: "5px solid var(--accent)",
                  paddingLeft: 14,
                }}
              >
                {t(
                  "Success looks like one thing: a person who keeps logging every day and can see themselves progressing.",
                  "Sucesso é uma coisa só: uma pessoa que continua registrando todo dia e consegue ver a própria evolução."
                )}
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 22,
                }}
              >
                {TAGS.map(([en, pt]) => (
                  <span
                    key={en}
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      border: "2px solid var(--fg)",
                      padding: "5px 9px",
                    }}
                  >
                    {t(en, pt)}
                  </span>
                ))}
              </div>
              <a
                href="http://nuwafit.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-press-sm"
                style={{
                  display: "inline-block",
                  fontSize: 14,
                  letterSpacing: "0.06em",
                  background: "var(--accent)",
                  color: "var(--bg)",
                  padding: "13px 18px",
                  boxShadow: "6px 6px 0 var(--fg)",
                }}
              >
                NUWAFIT.COM ↗
              </a>
            </div>
            <div
              className="project-feature__shot"
              style={{
                background: "var(--bg)",
                padding: 26,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <img
                src={nuwaAppEn}
                alt="Nüwa app home screen: hydration, calories and today's meals"
                style={{
                  width: "100%",
                  maxWidth: 280,
                  border: "3px solid var(--fg)",
                  boxShadow: "8px 8px 0 var(--accent)",
                  display: "block",
                }}
              />
            </div>
          </div>
        </div>

        <a
          href="https://github.com/r-drg"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-press"
          style={{
            display: "block",
            minWidth: 0,
            background: "var(--fg)",
            color: "var(--bg)",
            padding: 24,
            boxShadow: "8px 8px 0 var(--fg)",
          }}
        >
          <div
            style={{
              fontFamily: "'Archivo Black',sans-serif",
              fontSize: 13,
              letterSpacing: "0.1em",
              marginBottom: 10,
            }}
          >
            02 · {t("THE REST", "O RESTO")}
          </div>
          <div
            style={{
              fontFamily: "'Archivo Black',sans-serif",
              fontSize: "clamp(22px,3vw,32px)",
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            {t(
              "Internal tools, agents & smart contracts",
              "Ferramentas internas, agentes e contratos inteligentes"
            )}
          </div>
          <p style={{ margin: "0 0 14px", fontSize: 14, lineHeight: 1.6 }}>
            {t(
              "Most of what I build lives behind company logins. The public leftovers (experiments, contracts, scrapers) are on GitHub.",
              "A maior parte do que eu construo vive atrás de logins corporativos. As sobras públicas (experimentos, contratos, scrapers) estão no GitHub."
            )}
          </p>
          <div style={{ fontFamily: "'Archivo Black',sans-serif", fontSize: 14 }}>
            GITHUB.COM/R-DRG ↗
          </div>
        </a>

      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "6px 14px",
          margin: "48px 0 8px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontFamily: "'Archivo Black',sans-serif",
            fontSize: "clamp(20px,3vw,28px)",
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
          }}
        >
          {t("Nintendo Switch homebrew & ports", "Homebrew e ports para Nintendo Switch")}
        </h3>
        <div style={{ flex: 1, minWidth: 40, height: 3, background: "var(--fg)" }} />
      </div>
      <p style={{ margin: "0 0 22px", fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        {t(
          "Native Switch builds, no emulators. The ports are static recompilations of the original console executables; bring your own copy of the game, the installers never upload it.",
          "Builds nativos para Switch, sem emuladores. Os ports são recompilações estáticas dos executáveis originais dos consoles; você usa sua própria cópia do jogo, e os instaladores nunca fazem upload dela."
        )}
      </p>
      <div className="switch-grid">
        {SWITCH_PROJECTS.map((project, i) => (
          <div
            key={project.name}
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              border: "3px solid var(--fg)",
              background: "var(--card)",
              boxShadow: "8px 8px 0 var(--accent)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
                borderBottom: "3px solid var(--fg)",
                padding: "8px 14px",
                background: "var(--fg)",
                color: "var(--bg)",
              }}
            >
              <span
                style={{
                  fontFamily: "'Archivo Black',sans-serif",
                  fontSize: 13,
                  letterSpacing: "0.1em",
                }}
              >
                {String(i + 3).padStart(2, "0")} · SWITCH
              </span>
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  background: "var(--bg)",
                  color: "var(--fg)",
                  padding: "4px 8px",
                }}
              >
                {t(...project.kind)}
              </span>
            </div>
            <div
              style={{
                padding: 22,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginBottom: 14,
                }}
              >
                <img
                  src={project.icon}
                  alt={`${project.name} icon`}
                  width={72}
                  height={72}
                  style={{
                    flexShrink: 0,
                    border: "3px solid var(--fg)",
                    display: "block",
                  }}
                />
                <h4
                  style={{
                    margin: 0,
                    fontFamily: "'Archivo Black',sans-serif",
                    fontSize: "clamp(20px,2.4vw,26px)",
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    textTransform: "uppercase",
                    minWidth: 0,
                  }}
                >
                  {project.name}
                </h4>
              </div>
              <p style={{ margin: "0 0 16px", fontSize: 14, lineHeight: 1.6 }}>
                {t(...project.description)}
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                {project.tags.map(([en, pt]) => (
                  <span
                    key={en}
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      border: "2px solid var(--fg)",
                      padding: "5px 9px",
                    }}
                  >
                    {t(en, pt)}
                  </span>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  marginTop: "auto",
                }}
              >
                <a
                  href={project.repo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-press-sm"
                  style={{
                    fontSize: 13,
                    letterSpacing: "0.06em",
                    background: "var(--accent)",
                    color: "var(--bg)",
                    padding: "10px 14px",
                    boxShadow: "5px 5px 0 var(--fg)",
                  }}
                >
                  GITHUB ↗
                </a>
                {project.installer && (
                  <a
                    href={project.installer}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-invert"
                    style={{
                      fontSize: 13,
                      letterSpacing: "0.06em",
                      color: "var(--fg)",
                      padding: "10px 14px",
                      boxShadow: "5px 5px 0 var(--accent)",
                    }}
                  >
                    {t("INSTALLER ↗", "INSTALADOR ↗")}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          minWidth: 0,
          marginTop: 36,
          border: "3px dashed var(--fg)",
          padding: 24,
          color: "var(--fg)",
        }}
      >
        <div
          style={{
            fontFamily: "'Archivo Black',sans-serif",
            fontSize: 13,
            letterSpacing: "0.1em",
            marginBottom: 10,
            color: "var(--accent)",
          }}
        >
          07 · {t("LOADING", "CARREGANDO")}
        </div>
        <div
          style={{
            fontFamily: "'Archivo Black',sans-serif",
            fontSize: "clamp(22px,3vw,32px)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
          }}
        >
          {t("Next one's in the oven", "O próximo está no forno")}
        </div>
        <p style={{ margin: "14px 0 0", fontSize: 14, lineHeight: 1.6 }}>
          {t(
            "Check back. Or just ask me about it.",
            "Volte depois. Ou simplesmente me pergunte."
          )}
        </p>
      </div>
    </section>
  );
}
